import React, { useState } from 'react';
import { FileText, FileSpreadsheet, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface ExportPayload {
  columns: string[];
  data: Record<string, string>[];
}

interface ExportButtonsProps {
  reportTitle: string;
  subtitle?: string;
  // Called at click time, not render time — so it can apply the same
  // trend/pivot reshaping the Export CSV button already applies, rather than
  // exporting the raw, un-reshaped data for report types that need reshaping.
  getExportData: () => ExportPayload;
}

// Applied automatically to every export, regardless of caller — matches the
// on-screen table convention (uppercase, no underscores) so no individual
// page has to remember to do this itself. Safe to apply even to columns that
// are already nicely formatted (e.g. "Variance (%)" -> "VARIANCE (%)").
function prettifyPayload(payload: ExportPayload): ExportPayload {
  const prettyMap = payload.columns.map(c => [c, c.replace(/_/g, ' ').toUpperCase()] as const);
  const prettyColumns = prettyMap.map(([, pretty]) => pretty);
  const prettyData = payload.data.map(row => {
    const newRow: Record<string, string> = {};
    prettyMap.forEach(([raw, pretty]) => { newRow[pretty] = row[raw] ?? ''; });
    return newRow;
  });
  return { columns: prettyColumns, data: prettyData };
}

export function ExportButtons({ reportTitle, subtitle, getExportData }: ExportButtonsProps) {
  const { getToken } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const doExport = async (action: 'export_pdf' | 'export_excel', kind: 'pdf' | 'excel') => {
    setExporting(kind);
    setError(null);
    try {
      const { columns, data } = prettifyPayload(getExportData());
      const token = await getToken();
      const res = await fetch(`${API_URL}/document-export`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, report_title: reportTitle, subtitle, columns, data }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error ?? `HTTP ${res.status}`);
      window.open(result.download_url, '_blank');
    } catch (e: any) {
      setError(e.message ?? 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const disabled = exporting !== null;

  return (
    <div className="flex items-center space-x-2">
      <button
        onClick={() => doExport('export_pdf', 'pdf')}
        disabled={disabled}
        className="flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {exporting === 'pdf' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
        <span>{exporting === 'pdf' ? 'Generating…' : 'Export PDF'}</span>
      </button>

      <button
        onClick={() => doExport('export_excel', 'excel')}
        disabled={disabled}
        className="flex items-center space-x-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {exporting === 'excel' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
        <span>{exporting === 'excel' ? 'Generating…' : 'Export Excel'}</span>
      </button>

      {error && (
        <span className="flex items-center space-x-1 text-xs text-red-600">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>{error}</span>
        </span>
      )}
    </div>
  );
}
